import {Component, computed, effect, inject, Injector, signal, viewChild, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatDialog} from "@angular/material/dialog"
import {ActivatedRoute, ParamMap, Router} from "@angular/router"
import {DialogComponent} from "@app/common/components/dialogs/dialog/dialog.component"
import {RoutedDialogComponent} from "@app/common/components/dialogs/routed-dialog/routed-dialog.component"
import {createLinkToEditorFromPictures, createLinkToEditorFromTemplates} from "@app/common/helpers/routes"
import {formatInconsistenciesMessage} from "@app/template-editor/components/template-editor/helpers"
import {DialogSize} from "@app/common/models/dialogs"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"
import {RefreshService} from "@app/common/services/refresh/refresh.service"
import {TemplateEditComponent} from "@app/template-editor/components/template-edit/template-edit.component"
import {CmLogger} from "@cm/log"
import {TemplateNodes, TemplateGraph, deserializeTemplateGraph} from "@cm/template-nodes"
import {LoadingSpinnerComponent} from "@common/components/progress/loading-spinner/loading-spinner.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {CM_LOGGER_TEMPLATES} from "@common/services/logging/index"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {ContentTypeModel, TemplateState, TemplateType} from "@generated"
import {
    CreateTemplateForTemplateEditorGQL,
    CreateTemplateRevisionForTemplateEditorGQL,
    GetLatestTemplateRevisionDetailsForTemplateEditorGQL,
    GetPictureDetailsForTemplateEditorGQL,
    GetTemplateDetailsForTemplateEditorGQL,
    GetTemplateDetailsForTemplateEditorQuery,
    GetTemplateRevisionDetailsForTemplateEditorGQL,
    UpdateTemplateRevisionForTemplateEditorGQL,
    UpdateTemplateForTemplateEditorGQL,
    UpdatePictureForTemplateEditorGQL,
} from "@template-editor/components/template-editor/template-editor.generated"
import {catchError, combineLatest, distinctUntilChanged, map, of, switchMap} from "rxjs"
import {z} from "zod"

const getUuidParam = (params: ParamMap, key: string) => {
    const result = z.string().uuid().safeParse(params.get(key))
    if (result.success) return result.data
    return null
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-editor",
    templateUrl: "./template-editor.component.html",
    styleUrl: "./template-editor.component.scss",
    imports: [RoutedDialogComponent, LoadingSpinnerComponent, TemplateEditComponent],
})
export class TemplateEditorComponent {
    readonly $templateGraphModified = signal(false)
    readonly $routedDialog = viewChild.required(RoutedDialogComponent)
    dialogSizes = DialogSize

    readonly permission = inject(PermissionsService)
    $can = this.permission.$to
    private readonly route = inject(ActivatedRoute)
    private readonly router = inject(Router)
    private readonly dialog = inject(MatDialog)
    private readonly notificationService = inject(NotificationsService)
    private readonly localStorageService = inject(LocalStorageService)
    readonly injector = inject(Injector)
    private readonly logger: CmLogger = inject(CM_LOGGER_TEMPLATES)
    readonly getTemplateRevisionDetailsForTemplateEditor = inject(GetTemplateRevisionDetailsForTemplateEditorGQL)
    readonly createTemplateRevisionForTemplateEditor = inject(CreateTemplateRevisionForTemplateEditorGQL)
    readonly getPictureDetailsForTemplateEditor = inject(GetPictureDetailsForTemplateEditorGQL)
    readonly getLatestTemplateRevisionDetailsForTemplateEditor = inject(GetLatestTemplateRevisionDetailsForTemplateEditorGQL)
    readonly updateTemplateRevisionForTemplateEditor = inject(UpdateTemplateRevisionForTemplateEditorGQL)
    readonly updateTemplateForTemplateEditor = inject(UpdateTemplateForTemplateEditorGQL)
    readonly updatePictureForTemplateEditor = inject(UpdatePictureForTemplateEditorGQL)
    readonly getTemplateDetailsForTemplateEditor = inject(GetTemplateDetailsForTemplateEditorGQL)
    readonly createTemplateForTemplateEditor = inject(CreateTemplateForTemplateEditorGQL)
    readonly refresh = inject(RefreshService)

    private readonly $routingData = toSignal(
        combineLatest([
            this.route.paramMap.pipe(
                map((params) => getUuidParam(params, "templateId")),
                distinctUntilChanged(),
            ),
            this.route.paramMap.pipe(
                map((params) => getUuidParam(params, "pictureId")),
                distinctUntilChanged(),
            ),
            this.route.paramMap.pipe(
                map((params) => getUuidParam(params, "templateRevisionId")),
                distinctUntilChanged(),
            ),
        ]).pipe(
            map(([templateId, pictureId, templateRevisionId]) => ({
                templateId,
                pictureId,
                templateRevisionId,
            })),
            catchError((error) => {
                this.logger.error("Error loading template", error)
                return of(null)
            }),
        ),
        {
            initialValue: null,
        },
    )
    readonly $templateRevision = toSignal(
        toObservable(this.$routingData).pipe(
            switchMap((routingData) => {
                if (!routingData) return of(null)
                const revisionId = routingData.templateRevisionId
                if (revisionId === null) return of(null)

                return this.refresh.observeItem$({id: revisionId, __typename: ContentTypeModel.TemplateRevision}, true).pipe(
                    switchMap(() => fetchThrowingErrors(this.getTemplateRevisionDetailsForTemplateEditor)({id: revisionId})),
                    map((result) => result?.templateRevision ?? null),
                    catchError(() => of(null)),
                )
            }),
        ),
        {initialValue: null},
    )
    readonly $revisionText = computed(() => {
        const templateRevision = this.$templateRevision()
        if (!templateRevision?.template) return undefined

        const {template} = templateRevision
        const {latestRevision} = template

        if (templateRevision.number !== latestRevision?.number) return `v${templateRevision.number}`
        return undefined
    })
    readonly $preTitle = computed(() => {
        const templateRevision = this.$templateRevision()
        if (!templateRevision?.template) return undefined

        const {template} = templateRevision
        const {picture} = template
        if (picture) {
            return `Picture ${picture.number}: `
        }

        return undefined
    })
    readonly $title = computed(() => {
        const templateRevision = this.$templateRevision()
        if (!templateRevision?.template) return undefined

        const {template} = templateRevision
        const {picture} = template
        if (picture) return picture.name
        else return template.name ?? undefined
    })
    readonly $allowTitleEdit = computed(() => {
        const templateRevision = this.$templateRevision()
        if (!templateRevision?.template) return false

        const {template} = templateRevision
        const {picture} = template
        if (picture) {
            return this.$can().update.picture(picture, "name")
        } else return this.$can().update.template(template, "name")
    })
    protected readonly $templateGraphWithId = computed(
        () => {
            const templateRevision = this.$templateRevision()
            if (!templateRevision) return {templateGraph: null, id: undefined}

            const templateGraph = (() => {
                const {graph} = templateRevision
                if (!graph) return new TemplateGraph({parameters: {name: "Untitled Template", nodes: new TemplateNodes({parameters: {list: []}})}})
                return deserializeTemplateGraph(graph)
            })()

            const {id} = templateRevision
            return {templateGraph, id}
        },
        {
            equal: (a, b) => {
                return a.id === b.id
            },
        },
    )
    protected readonly $templateGraph = computed(() => {
        return this.$templateGraphWithId().templateGraph
    })

    constructor() {
        effect(async () => {
            const routingData = this.$routingData()
            if (!routingData) return

            const {templateId, pictureId, templateRevisionId} = routingData

            const getNewRouterPath = async () => {
                const getOrCreateLatestTemplateRevision = async (template: GetTemplateDetailsForTemplateEditorQuery["template"]) => {
                    const {latestRevision, id: templateId} = template

                    if (latestRevision) return latestRevision.id
                    else {
                        const savedRevision = (
                            await mutateThrowingErrors(this.createTemplateRevisionForTemplateEditor)({
                                input: {templateId: templateId, graph: null, completed: false},
                            })
                        ).createTemplateRevision
                        return savedRevision.id
                    }
                }

                if (templateId !== null && templateRevisionId === null) {
                    const {template} = await fetchThrowingErrors(this.getTemplateDetailsForTemplateEditor)({id: templateId})
                    return createLinkToEditorFromTemplates(this.router, templateId, await getOrCreateLatestTemplateRevision(template))
                } else if (pictureId !== null && templateRevisionId === null) {
                    const {picture} = await fetchThrowingErrors(this.getPictureDetailsForTemplateEditor)({id: pictureId})
                    const {template} = picture

                    if (!template) {
                        const savedTemplate = (
                            await mutateThrowingErrors(this.createTemplateForTemplateEditor)({
                                input: {
                                    organizationId: picture.organization.id,
                                    state: TemplateState.Draft,
                                    templateType: TemplateType.Picture,
                                    public: false,
                                    pictureId: picture.id,
                                },
                            })
                        ).createTemplate

                        const savedRevision = (
                            await mutateThrowingErrors(this.createTemplateRevisionForTemplateEditor)({
                                input: {templateId: savedTemplate.id, graph: null, completed: false},
                            })
                        ).createTemplateRevision
                        return createLinkToEditorFromPictures(pictureId, savedRevision.id)
                    } else {
                        return createLinkToEditorFromPictures(pictureId, await getOrCreateLatestTemplateRevision(template))
                    }
                } else return null
            }

            const newRouterPath = await getNewRouterPath()
            if (newRouterPath !== null)
                void this.router.navigate(newRouterPath, {
                    queryParamsHandling: "preserve",
                })
        })

        if (!this.localStorageService.developmentMode) {
            effect(async () => {
                const templateRevision = this.$templateRevision()
                if (templateRevision?.inconsistencies?.length) {
                    const dialogRef = this.dialog.open(DialogComponent, {
                        disableClose: true,
                        width: "400px",
                        data: {
                            cancelLabel: this.$can().open.inconsistentTemplate() ? "Ignore" : undefined,
                            confirmLabel: "Close Editor",
                            message: formatInconsistenciesMessage(templateRevision.inconsistencies),
                            title: "Data inconsistencies",
                        },
                    })
                    dialogRef.afterClosed().subscribe(async (result) => {
                        if (result) {
                            await this.router.navigate([this.route.snapshot.data.closeNavigationPath ?? "../"], {
                                relativeTo: this.route,
                                queryParamsHandling: "preserve",
                            })
                        }
                    })
                }
            })
        }

        effect(async () => {
            const templateRevision = this.$templateRevision()
            if (!templateRevision?.template) return

            const canEdit = this.$can().update.templateRevision({template: templateRevision.template})
            if (!canEdit) {
                const dialogRef = this.dialog.open(DialogComponent, {
                    disableClose: true,
                    width: "400px",
                    data: {
                        confirmLabel: "Close Editor",
                        message: "You do not have permission to edit this template.",
                        title: "Permission denied",
                    },
                })
                dialogRef.afterClosed().subscribe(async () => {
                    await this.router.navigate([this.route.snapshot.data.closeNavigationPath ?? "../"], {
                        relativeTo: this.route,
                        queryParamsHandling: "preserve",
                    })
                })
            }
        })
    }

    handleTemplateGraphModified(modified: boolean) {
        this.$templateGraphModified.set(modified)
    }

    handleClose() {
        void this.$routedDialog().closeIfClean()
    }

    async handleSave(onSaved?: () => void) {
        const templateRevision = this.$templateRevision()
        if (!templateRevision) throw new Error("No template revision loaded")
        const templateGraph = this.$templateGraph()
        if (!templateGraph) throw new Error("No template graph loaded")

        const savedRevision = (
            await mutateThrowingErrors(this.updateTemplateRevisionForTemplateEditor)({
                input: {
                    id: templateRevision.id,
                    graph: templateGraph.serialize(),
                },
            })
        ).updateTemplateRevision

        this.notificationService.showInfo("Template revision saved")

        this.refresh.item({...savedRevision, __typename: ContentTypeModel.TemplateRevision})

        onSaved?.()
    }

    async handleSaveNewRevision(onSaved?: () => void) {
        const routingData = this.$routingData()
        if (!routingData) return
        const {templateId, pictureId} = routingData
        if (templateId === null && pictureId === null) return

        const templateRevision = this.$templateRevision()
        if (!templateRevision) throw new Error("No template revision loaded")
        if (!templateRevision.template) throw new Error("Cannot save new revision: template is not accessible")
        const templateGraph = this.$templateGraph()
        if (!templateGraph) throw new Error("No template graph loaded")

        const savedRevision = (
            await mutateThrowingErrors(this.createTemplateRevisionForTemplateEditor)({
                input: {templateId: templateRevision.template.id, graph: templateGraph.serialize(), completed: false},
            })
        ).createTemplateRevision

        this.notificationService.showInfo("New template revision saved")

        if (savedRevision.template) this.refresh.item({...savedRevision.template, __typename: ContentTypeModel.Template})
        this.refresh.item({...savedRevision, __typename: ContentTypeModel.TemplateRevision})

        if (templateId !== null) {
            await this.router.navigate(createLinkToEditorFromTemplates(this.router, templateId, savedRevision.id), {
                queryParamsHandling: "preserve",
            })
        } else if (pictureId !== null) {
            await this.router.navigate(createLinkToEditorFromPictures(pictureId, savedRevision.id), {
                queryParamsHandling: "preserve",
            })
        }

        onSaved?.()
    }

    async handleTitleChanged(newTitle: string) {
        const templateRevision = this.$templateRevision()
        if (!templateRevision) throw new Error("No template revision loaded")

        const {template} = templateRevision
        if (!template) throw new Error("Cannot edit title: template is not accessible")
        const {picture} = template
        if (picture) {
            const savedPicture = (
                await mutateThrowingErrors(this.updatePictureForTemplateEditor)({
                    input: {id: picture.id, name: newTitle},
                })
            ).updatePicture
            this.notificationService.showInfo("Picture name updated")
            this.refresh.item({...savedPicture, __typename: ContentTypeModel.Picture})
            this.refresh.item({id: templateRevision.id, __typename: ContentTypeModel.TemplateRevision})
        } else {
            const savedTemplate = (
                await mutateThrowingErrors(this.updateTemplateForTemplateEditor)({
                    input: {id: template.id, name: newTitle},
                })
            ).updateTemplate
            this.notificationService.showInfo("Template name updated")
            this.refresh.item({...savedTemplate, __typename: ContentTypeModel.Template})
            this.refresh.item({id: templateRevision.id, __typename: ContentTypeModel.TemplateRevision})
        }
    }
}
